import { useState } from "react";

import { type OnEventHandler } from "react-lib/appcon/common/SearchBoxDropdown";

import { ButtonV4, SelectV4, type SelectV4Option } from "../common/ui";
import {
  type DoctorOption,
  type Icd9Option,
  type OrderMasterOptions,
  type SheetLine,
} from "../common/sequence/OrderSheetV4";
import { DRUG_PRIORITIES } from "./ordersModelV4";
import {
  drugFactText,
  type Field,
  isIcdUnpicked,
  isQtyMissing,
  masterOptionsOf,
  visibleFields,
} from "./orderSheetLineModelV4";

// Types
type SheetLineFieldsProps = {
  line: SheetLine;
  doctorOptions: DoctorOption[];
  masterOptions?: OrderMasterOptions;
  icd9Options: Icd9Option[];
  searchedItemListWithKey?: Record<string, any>;
  // callback
  onEvent: OnEventHandler;
  onChange: (fields: Record<string, string>) => void;
  onSearchIcd9: (query: string) => void;
  onClose: () => void;
};

type SheetLineControlsProps = {
  line: SheetLine;
  // callback
  onChange: (data: {
    quantity?: number;
    priceUnit?: number;
    fields?: Record<string, string>;
  }) => void;
};

// Const
const QTY_KINDS = new Set<SheetLine["kind"]>(["imaging", "treatment", "supply", "misc"]);

const PRICE_KINDS = new Set<SheetLine["kind"]>(["df", "misc"]);

/* ------------------------------------------------------ */

/*                       Components                       */

/* ------------------------------------------------------ */
export const SheetLineControls = (props: SheetLineControlsProps) => {
  const { line } = props;

  return (
    <>
      {(line.kind === "supply" || line.kind === "drug") && (
        <label className={`hv4-ord-home${line.fields.home === "Y" ? " hv4-ord-home-on" : ""}`}>
          <input
            type="checkbox"
            checked={line.fields.home === "Y"}
            disabled={line.fields.priority === "STAT"}
            onChange={(e) => props.onChange({ fields: { home: e.target.checked ? "Y" : "N" } })}
          />
          จ่ายกลับบ้าน
        </label>
      )}
      {QTY_KINDS.has(line.kind) && (
        <span className="hv4-ord-qty">
          <ButtonV4
            variant="ghost"
            size="icon"
            aria-label="ลดจำนวน"
            onClick={() => props.onChange({ quantity: Math.max(1, line.quantity - 1) })}
          >
            −
          </ButtonV4>
          <b>{line.quantity}</b>
          <ButtonV4
            variant="ghost"
            size="icon"
            aria-label="เพิ่มจำนวน"
            onClick={() => props.onChange({ quantity: line.quantity + 1 })}
          >
            +
          </ButtonV4>
        </span>
      )}
      {PRICE_KINDS.has(line.kind) && (
        <span className="hv4-ord-price">
          {line.kind === "df" && (
            <span className="hv4-ord-price-hint">ราคา Master {line.masterPrice} ฿</span>
          )}
          ราคา/หน่วย
          <input
            className="hv4-input"
            aria-label="ราคาต่อหน่วย (บาท)"
            inputMode="decimal"
            value={line.priceUnit}
            onChange={(e) => props.onChange({ priceUnit: Number(e.target.value) || 0 })}
          />
        </span>
      )}
    </>
  );
};

export const SheetLineFields = (props: SheetLineFieldsProps) => {
  const { line } = props;

  const [info, setInfo] = useState<"fact" | "solvent" | null>(null);

  const solvents: string[] = line.raw.solvents ?? [];

  const selectOptions = (field: Field): SelectV4Option[] =>
    field.master
      ? masterOptionsOf(line, field.master, props.masterOptions)
      : (field.options ?? []).map(([value, text]) => ({ value, text }));

  const handleChange = (key: string, value: string) => {
    if (key !== "icd") {
      props.onChange({ [key]: value });

      return;
    }

    const picked = props.icd9Options.find((option) => option.label === value);

    props.onChange({ icd: value, icd9: picked ? JSON.stringify(picked) : "" });

    if (!picked) {
      props.onSearchIcd9(value);
    }
  };

  return (
    <div className="hv4-ord-fields">
      {line.kind === "drug" && (
        <div className="hv4-ord-priority">
          <label className="hv4-label" htmlFor={`hv4_ord_${line.key}_priority`}>
            Priority
          </label>
          <SelectV4
            id={`hv4_ord_${line.key}_priority`}
            size="sm"
            className={line.fields.priority === "STAT" ? "hv4-ord-priority-stat" : undefined}
            options={DRUG_PRIORITIES.map(([value, text]) => ({ value, text }))}
            value={line.fields.priority || "ROUTINE"}
            onChange={(next) =>
              props.onChange(next === "STAT" ? { priority: next, home: "N" } : { priority: next })
            }
          />
        </div>
      )}
      <div className="hv4-ord-fields-row">
        {visibleFields(line).map((field) => {
          const id = `hv4_ord_${line.key}_${field.key}`;
          const value = line.fields[field.key] ?? "";
          const isEmpty =
            (!!field.required && !value.trim()) || (field.key === "icd" && isIcdUnpicked(line));
          const options = field.options || field.master ? selectOptions(field) : null;

          return (
            <div
              key={field.key}
              className={
                typeof field.width === "number"
                  ? "hv4-ord-field"
                  : `hv4-ord-field hv4-ord-field-${field.width}`
              }
              style={typeof field.width === "number" ? { width: field.width } : undefined}
            >
              <label className="hv4-label" htmlFor={id}>
                {field.required ? `${field.label} *` : field.label}
                {!!field.maxLength && ` [${value.length}/${field.maxLength}]`}
              </label>
              {field.key === "employee" ? (
                <div className={`hv4-search-dd${isEmpty ? " hv4-search-dd-invalid" : ""}`}>
                  <SelectV4
                    id={id}
                    search
                    options={props.doctorOptions.map((doctor) => ({
                      value: doctor.employee,
                      text: doctor.name,
                    }))}
                    value={
                      props.doctorOptions.some((doctor) => String(doctor.employee) === value)
                        ? value
                        : ""
                    }
                    placeholder={field.placeholder}
                    onChange={(next) => handleChange(field.key, next)}
                  />
                </div>
              ) : field.suggest ? (
                <div className="hv4-ord-combo">
                  <input
                    id={id}
                    className="hv4-input hv4-input-sm"
                    list={`${id}_list`}
                    aria-invalid={isEmpty}
                    value={value}
                    onChange={(e) => handleChange(field.key, e.target.value)}
                  />
                  <datalist id={`${id}_list`}>
                    {(props.masterOptions?.[field.suggest] ?? []).map((option) => (
                      <option key={String(option.value)} value={option.text} />
                    ))}
                  </datalist>
                  {!!value && (
                    <ButtonV4
                      variant="ghost"
                      size="icon"
                      aria-label="ล้างค่า"
                      onClick={() => handleChange(field.key, "")}
                    >
                      ✕
                    </ButtonV4>
                  )}
                </div>
              ) : options ? (
                <SelectV4
                  id={id}
                  size="sm"
                  search={options.length > 8}
                  invalid={isEmpty}
                  placeholder={field.placeholder ?? "— เลือก —"}
                  options={options}
                  value={value}
                  onChange={(next) => handleChange(field.key, next)}
                />
              ) : field.combo ? (
                <div className="hv4-ord-combo">
                  <SelectV4
                    id={id}
                    size="sm"
                    search
                    invalid={isEmpty}
                    placeholder={field.placeholder}
                    options={props.icd9Options.map((option) => ({
                      value: option.label,
                      text: option.label,
                    }))}
                    value={value}
                    onSearch={props.onSearchIcd9}
                    onChange={(next) => handleChange(field.key, next)}
                  />
                  {!!value && (
                    <ButtonV4
                      variant="ghost"
                      size="icon"
                      aria-label="ล้างค่า"
                      onClick={() => handleChange(field.key, "")}
                    >
                      ✕
                    </ButtonV4>
                  )}
                </div>
              ) : (
                <input
                  id={id}
                  className="hv4-input hv4-input-sm"
                  aria-invalid={isEmpty || (field.key === "qty" && isQtyMissing(line))}
                  inputMode={field.numeric ? "decimal" : undefined}
                  maxLength={field.maxLength}
                  placeholder={
                    field.key === "qty" ? `auto ${line.quantity || 0}` : field.placeholder
                  }
                  value={value}
                  onChange={(e) => handleChange(field.key, e.target.value)}
                />
              )}
            </div>
          );
        })}
      </div>
      {line.kind === "drug" && (
        <div className="hv4-ord-drug-info">
          <div className="hv4-ord-drug-info-btns">
            <ButtonV4
              variant="ghost"
              size="sm"
              className="hv4-ord-fact-btn"
              selected={info === "fact"}
              onClick={() => setInfo(info === "fact" ? null : "fact")}
            >
              DRUG FACT
            </ButtonV4>
            <ButtonV4
              variant="ghost"
              size="sm"
              className="hv4-ord-solv-btn"
              selected={info === "solvent"}
              onClick={() => setInfo(info === "solvent" ? null : "solvent")}
            >
              SOLVENT
            </ButtonV4>
          </div>
          {info === "fact" && <div className="hv4-ord-fact">{drugFactText(line)}</div>}
          {info === "solvent" && (
            <div className="hv4-ord-solv">
              <b>{solvents.length > 0 ? "Solvent ที่แนะนำ: " : "ยานี้ไม่มี Solvent ใน Master"}</b>
              {solvents.map((name) => (
                <button
                  key={name}
                  type="button"
                  className="hv4-ord-solv-pick"
                  onClick={() => props.onChange({ description: `Solvent: ${name}`.slice(0, 60) })}
                >
                  {name}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      <div className="hv4-ord-fields-foot">
        <ButtonV4 variant="primary" onClick={props.onClose}>
          บันทึก
        </ButtonV4>
      </div>
    </div>
  );
};

SheetLineControls.displayName = "SheetLineControls";
SheetLineFields.displayName = "SheetLineFields";
